/**
 * Custom celestial icons — proprietary glyphs for the 3 landing hooks.
 *
 * Style direction (from the brand brief):
 *   • Thin gold strokes (1.4–1.6px) with soft white highlights
 *   • Purple glow drop-shadow on hover
 *   • Geometric balance — constellation-inspired nodes, orbital arcs, star centers
 *   • Never look like free icon-library assets
 *
 * Each icon ships at 48px default, scales freely (size prop).
 */

const COMMON_PROPS = {
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round",
};

/**
 * Ask the Universe — a question mark formed from a continuous star-trail loop.
 * Dotted curve drawn with progressively brighter dots, terminated by a 4-point star center.
 */
export function IconAskUniverse({ size = 48, className = "" }) {
    return (
        <svg
            viewBox="0 0 48 48"
            width={size}
            height={size}
            aria-hidden="true"
            className={className}
            {...COMMON_PROPS}
        >
            {/* Star-trail curve forming a "?" — dotted path */}
            <path
                d="M14 17 Q14 9 24 9 Q34 9 34 17 Q34 23 27 26 Q24 27.5 24 31"
                stroke="#F1D78A"
                strokeWidth="1.6"
                strokeDasharray="0.4 2.4"
                opacity="0.85"
            />
            {/* Bright star nodes along the trail */}
            <circle cx="18"   cy="11.5" r="1.0" fill="#FFE9A6" />
            <circle cx="27"   cy="9.5"  r="1.2" fill="#FFE9A6" />
            <circle cx="33"   cy="14"   r="1.0" fill="#F1D78A" />
            <circle cx="30"   cy="22"   r="1.1" fill="#FFE9A6" />
            <circle cx="24.5" cy="28"   r="1.0" fill="#F1D78A" />

            {/* The dot of the question mark — a 4-point gold star */}
            <g transform="translate(24 38)">
                <path
                    d="M0 -3.6 L0.9 -0.9 L3.6 0 L0.9 0.9 L0 3.6 L-0.9 0.9 L-3.6 0 L-0.9 -0.9 Z"
                    fill="#D4AF37"
                    stroke="#FFE9A6"
                    strokeWidth="0.5"
                />
                <circle cx="0" cy="0" r="6" fill="none"
                        stroke="#FFE9A6" strokeWidth="0.35" opacity="0.55" />
            </g>
        </svg>
    );
}

/**
 * Check Your Match — two distinct star nodes connected by a delicate
 * constellation path (dashed gold line + small orbital arcs).
 */
export function IconCheckMatch({ size = 48, className = "" }) {
    return (
        <svg
            viewBox="0 0 48 48"
            width={size}
            height={size}
            aria-hidden="true"
            className={className}
            {...COMMON_PROPS}
        >
            {/* Outer orbital rings (very faint) */}
            <ellipse cx="14" cy="24" rx="9" ry="6"
                     stroke="rgba(241,215,138,0.30)" strokeWidth="0.55" />
            <ellipse cx="34" cy="24" rx="9" ry="6"
                     stroke="rgba(241,215,138,0.30)" strokeWidth="0.55" />

            {/* Constellation line between the two */}
            <path
                d="M14 24 Q24 12 34 24"
                stroke="#F1D78A"
                strokeWidth="1.0"
                strokeDasharray="1.8 1.6"
                opacity="0.9"
            />

            {/* Two 4-point stars */}
            <g transform="translate(14 24)">
                <path
                    d="M0 -4.4 L1.1 -1.1 L4.4 0 L1.1 1.1 L0 4.4 L-1.1 1.1 L-4.4 0 L-1.1 -1.1 Z"
                    fill="#FFE9A6"
                    stroke="#D4AF37"
                    strokeWidth="0.5"
                />
            </g>
            <g transform="translate(34 24)">
                <path
                    d="M0 -4.4 L1.1 -1.1 L4.4 0 L1.1 1.1 L0 4.4 L-1.1 1.1 L-4.4 0 L-1.1 -1.1 Z"
                    fill="#FFE9A6"
                    stroke="#D4AF37"
                    strokeWidth="0.5"
                />
            </g>

            {/* Tiny midpoint sparkle */}
            <circle cx="24" cy="15.2" r="0.9" fill="#FFE9A6" />
        </svg>
    );
}

/**
 * Today's Prediction — a 4-point gold star rising above a curved horizon arc,
 * with sparkle dust and a soft sun radiance behind it.
 */
export function IconTodayPrediction({ size = 48, className = "" }) {
    return (
        <svg
            viewBox="0 0 48 48"
            width={size}
            height={size}
            aria-hidden="true"
            className={className}
            {...COMMON_PROPS}
        >
            {/* Radiance halo behind the star */}
            <circle cx="24" cy="20" r="10" fill="none"
                    stroke="rgba(255,233,166,0.20)" strokeWidth="0.6" />
            <circle cx="24" cy="20" r="6" fill="rgba(255,233,166,0.18)" />

            {/* The rising star */}
            <g transform="translate(24 20)">
                <path
                    d="M0 -7.5 L1.8 -1.8 L7.5 0 L1.8 1.8 L0 7.5 L-1.8 1.8 L-7.5 0 L-1.8 -1.8 Z"
                    fill="#D4AF37"
                    stroke="#FFE9A6"
                    strokeWidth="0.7"
                />
            </g>

            {/* The horizon arc — gold rim against the purple */}
            <path
                d="M5 36 Q24 28 43 36"
                stroke="#D4AF37"
                strokeWidth="1.4"
                opacity="0.95"
            />
            <path
                d="M5 38.5 Q24 31 43 38.5"
                stroke="rgba(241,215,138,0.40)"
                strokeWidth="0.8"
            />

            {/* Sparkle dust around the star */}
            <circle cx="13" cy="15" r="0.7" fill="#FFE9A6" />
            <circle cx="36" cy="14" r="0.6" fill="#F1D78A" />
            <circle cx="33" cy="9"  r="0.5" fill="#FFE9A6" />
            <circle cx="16" cy="10" r="0.5" fill="#F1D78A" />
        </svg>
    );
}
